import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';

import { isUsableName, isValidContactEmail } from './registration-details';

/**
 * Fields only — the surrounding modal, title and buttons belong to the caller, because the
 * ABS Modal service needs a TemplateRef declared in the calling component's template.
 *
 * Used by registering, by editing an existing registration, and (name only) by the two
 * organiser name prompts. One component so the validation cannot drift between them: if
 * editing accepted what registering rejected, the edit path would become a way to put an
 * email address back into displayName.
 */
@Component({
    standalone: true,
    selector: 'abs-registration-details-form',
    imports: [IonicModule, TranslateModule],
    template: `
        <div class="form">
            <div class="form-group">
                <label>{{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_NAME' | translate }}</label>
                <ion-input
                    type="text"
                    [value]="nameValue()"
                    (ionInput)="onName($any($event).target.value)"
                    [placeholder]="'GLOBAL.EVENTS.REGISTRATION.DETAILS_NAME_PLACEHOLDER' | translate"
                ></ion-input>
                @if (showNameError()) {
                    <p class="field-error">{{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_NAME_ERROR' | translate }}</p>
                }
            </div>

            @if (showContactEmail) {
                <div class="form-group">
                    <label>{{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_EMAIL' | translate }}</label>
                    <ion-input
                        type="email"
                        [value]="emailValue()"
                        (ionInput)="onEmail($any($event).target.value)"
                    ></ion-input>
                    <p class="field-hint">{{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_EMAIL_HINT' | translate }}</p>
                    @if (showEmailError()) {
                        <p class="field-error">{{ 'GLOBAL.EVENTS.REGISTRATION.DETAILS_EMAIL_ERROR' | translate }}</p>
                    }
                </div>
            }
        </div>
    `
    // Styles live in army-builder-shared/src/lib/global/styles/events.scss, per the
    // workspace rule against CSS in component files.
})
export class RegistrationDetailsFormComponent {
    // These setters deliberately do NOT emit validChange. Emitting an output while Angular is
    // setting inputs fires during the parent's change-detection pass and produces
    // ExpressionChangedAfterItHasBeenCheckedError on the parent's [disabled] binding. Callers
    // seed their own initial validity instead — Tasks 6, 7 and 10 all do, before opening the
    // modal. validChange is only for edits the user makes.
    @Input() set name(value: string) {
        this.nameValue.set(value ?? '');
    }
    @Input() set contactEmail(value: string) {
        this.emailValue.set(value ?? '');
    }
    /** False for the two organiser name prompts, which have no contact email to collect. */
    @Input() showContactEmail = true;

    @Output() nameChange = new EventEmitter<string>();
    @Output() contactEmailChange = new EventEmitter<string>();
    @Output() validChange = new EventEmitter<boolean>();

    readonly nameValue = signal('');
    readonly emailValue = signal('');

    // Errors appear only after the field has been edited, so a form that opens with an empty
    // name (a user who has none - the common case this feature exists for) does not greet
    // them with a red error before they have typed anything.
    private readonly nameTouched = signal(false);
    private readonly emailTouched = signal(false);

    readonly showNameError = computed(() => this.nameTouched() && !isUsableName(this.nameValue()));
    readonly showEmailError = computed(
        () => this.showContactEmail && this.emailTouched() && !isValidContactEmail(this.emailValue())
    );

    onName(value: string) {
        this.nameValue.set(value ?? '');
        this.nameTouched.set(true);
        this.nameChange.emit(this.nameValue());
        this.emitValid();
    }

    onEmail(value: string) {
        this.emailValue.set(value ?? '');
        this.emailTouched.set(true);
        this.contactEmailChange.emit(this.emailValue());
        this.emitValid();
    }

    private emitValid() {
        const valid = isUsableName(this.nameValue()) && (!this.showContactEmail || isValidContactEmail(this.emailValue()));
        this.validChange.emit(valid);
    }
}
